import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import IconSuccess from "./IconSuccess";
import IconWarning from "./IconWarning";
import {
  setIsFinanceConfigured,
  setFinanceType,
  setFinanceSubtype,
  setCanonCopyPlanType,
  setCfaAmount,
  setTotalAmountFinanced,
  setAdjustmentPeriod,
  setCanonFinancePayout,
  setCfaCcBw,
  setCfaCcBwAdjustClickChargePerMonthExclGst,
  setCfaCcBwMinimumMonthlyClickVolume,
  setCfaCcBwServiceCopyCost,
  setCfaCcBwExcessFinancedCopyCost,
  setCfaCcColour,
  setCfaCcColourAdjustClickChargePerMonthExclGst,
  setCfaCcColourMinimumMonthlyClickVolume,
  setCfaCcColourServiceCopyCost,
  setCfaCcColourExcessFinancedCopyCost,
  setFinanceTerm,
  setServiceTerm,
  setInstalmentAmount,
  setInstalmentType,
  setOriginatorReference,
  setPayableIn,
  setResidualValue,
  setThirdPartyPayout,
  setDeferredPayment,
  setQuoteItemUserClickVolumeBw,
  setQuoteItemUserClickVolumeColour
} from "../redux/actions";
import { fetchFinanceQuote } from "../api";
import {
  cfaCcBwTotalCostExclGst,
  cfaCcColourTotalCostExclGst,
  cfaCcBwAgreedTotalClickVolume,
  cfaCcColourAgreedTotalClickVolume,
  cfaBaseCharge,
  cfaCcBwExcessTotalCostExclGst,
  cfaCcColourExcessTotalCostExclGst,
  isLease,
  isPppCombined,
  isPppStandard,
  isPmp,
  isOriginatorReferenceValid,
  isPppOrPmp,
  topLevelHardwareItemsWithClickCharge,
  userTotalClickVolumeBw,
  userTotalClickVolumeColour,
  recommendServiceCopyCostBw,
  recommendServiceCopyCostColour,
  validateServiceCopyCost
} from "../redux/selectors";

import { formatMoney } from "accounting-js";
import { OnBlurInput } from "./OnBlurInput";
import PayoutInfoTable from "./PayoutInfoTable";
import { Form, Row, Col, InputGroup, Button, Table } from "react-bootstrap";

import { ADJUSTMENT_PERIODS, IS_PROD, IS_NZ } from "../constants";

class Originator extends Component {
  constructor(props) {
    super(props);

    this.state = {
      errorMessage: ""
    };
  }

  handleOriginatorReferenceChange = e => {
    this.setState({ errorMessage: "" });

    this.props.setOriginatorReference(e.target.value);
    this.props.setIsFinanceConfigured(false);
  };

  handleLoadFinanceQuote = async e => {
    e.preventDefault();

    let response;
    try {
      const [quoteId, optionId] = this.props.originatorReference.split("-", 2);
      response = await fetchFinanceQuote(quoteId, optionId);

      if (response.Error) {
        this.setState({ errorMessage: response.Error.message });
        return;
      }
    } catch (error) {
      this.setState({
        errorMessage: `Failed to connect to the originator: ${error}`
      });
      return;
    }

    const {
      finance_subtype,
      canon_copy_plan_type,
      cfa_amount,
      total_amount_financed,
      canon_finance_payout,

      cfa_cc_b_w,
      cfa_cc_b_w_service_copy_cost,
      cfa_cc_b_w_minimum_monthly_click_volume,

      cfa_cc_colour,
      cfa_cc_colour_service_copy_cost,
      cfa_cc_colour_minimum_monthly_click_volume,

      finance_term,
      instalment_amount,
      instalment_type,
      payable_in,
      residual_value,
      third_party_payout,

      months_deferred
    } = response.Payload;

    this.props.setFinanceSubtype(finance_subtype);
    // this.props.setFinanceSubtype("PMP");
    this.props.setDeferredPayment(months_deferred);

    this.props.setCfaAmount(cfa_amount);
    this.props.setTotalAmountFinanced(total_amount_financed);
    this.props.setCanonFinancePayout(canon_finance_payout);
    this.props.setThirdPartyPayout(third_party_payout);

    this.props.setFinanceTerm(finance_term);
    this.props.setServiceTerm(finance_term);
    this.props.setInstalmentAmount(instalment_amount);
    this.props.setInstalmentType(instalment_type);
    this.props.setPayableIn(payable_in);

    // Lease only
    this.props.setResidualValue(residual_value);

    // region PPP only
    this.props.setCanonCopyPlanType(canon_copy_plan_type);

    this.props.setCfaCcBw(cfa_cc_b_w);
    this.props.setCfaCcBwServiceCopyCost(cfa_cc_b_w_service_copy_cost);
    this.props.setCfaCcBwMinimumMonthlyClickVolume(
      cfa_cc_b_w_minimum_monthly_click_volume
    );

    this.props.setCfaCcColour(cfa_cc_colour);
    this.props.setCfaCcColourServiceCopyCost(cfa_cc_colour_service_copy_cost);
    this.props.setCfaCcColourMinimumMonthlyClickVolume(
      cfa_cc_colour_minimum_monthly_click_volume
    );
    // endregion

    this.props.setIsFinanceConfigured(true);
  };

  render() {
    return (
      <Fragment>
        <Form.Group as={Row} controlId="originatorReference">
          <Form.Label sm={3} column>
            Originator Reference
          </Form.Label>
          <Col sm={6}>
            <InputGroup>
              <Form.Control
                as="input"
                type="text"
                value={this.props.originatorReference}
                onChange={this.handleOriginatorReferenceChange}
                placeholder="1234-19"
              />
              <InputGroup.Append>
                <Button
                  disabled={!this.props.isOriginatorReferenceValid}
                  onClick={this.handleLoadFinanceQuote}
                >
                  Load the Finance Quote from Originator
                </Button>
                <Button
                  variant="secondary"
                  as="a"
                  href={IS_PROD ? "https://cfa.canon.com.au/ats/" : "https://uatoriginator.canon.com.au/ats/"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Originator in a New Window
                </Button>
              </InputGroup.Append>
            </InputGroup>

            <span className="form-text text-danger">
              {this.state.errorMessage}
            </span>
            <span className="form-text text-muted">
              The originator reference expects the format{" "}
              <code>QuoteId-OptionId</code>. e.g. <code>1234-19</code>
            </span>
          </Col>
        </Form.Group>

        {this.props.isPppOrPmp && (
          <Fragment>
            {this.props.isPmp ? <h3>PMP (Print Management Plan)</h3> : <h3>PPP (Payment Per Print)</h3> }
            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Adjustment Period
              </Form.Label>
              <Col sm={3}>
                <Form.Control
                  as='select'
                  onChange={ e => this.props.setAdjustmentPeriod(e.target.value)}
                  value={this.props.adjustmentPeriod}
                >
                  { ADJUSTMENT_PERIODS.map( p => <option key={p.value} value={p.value}>{ p.label }</option> )}
                </Form.Control>
              </Col>
            </Form.Group>
          </Fragment>
        )}

        {this.props.isConfigured && (
          <Fragment>
            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Finance Subtype
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">
                  {this.props.financeSubtype}
                </p>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Total Amount Financed
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">
                  {formatMoney(this.props.totalAmountFinanced)}
                </p>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                CF Amount
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">
                  {formatMoney(this.props.cfaAmount)}
                </p>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Canon Finance Payout
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">
                  {formatMoney(this.props.canonFinancePayout)}
                </p>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Third Party Payout
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">
                  {formatMoney(this.props.thirdPartyPayout)}
                </p>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Total Payout
              </Form.Label>
              <Col sm={9}>
                <p className="form-control-plaintext">
                  Total Amount Financed - CF Amount ={" "}
                  {formatMoney(this.props.totalAmountFinanced)} -{" "}
                  {formatMoney(this.props.cfaAmount)} ={" "}
                  {formatMoney(
                    this.props.totalAmountFinanced - this.props.cfaAmount
                  )}
                </p>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Finance Term
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">
                  {this.props.financeTerm} Months
                </p>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Fixed Rental Installment excl. GST
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">
                  {`${formatMoney(this.props.instalmentAmount)} ${
                    this.props.instalmentType
                  }`}
                </p>
              </Col>
            </Form.Group>

            {this.props.isPmp && (
              <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Base Charge
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">
                  {`${formatMoney(this.props.cfaBaseCharge / 100)}`}
                </p>
              </Col>
            </Form.Group>
            )}

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Payable Method
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">{this.props.payableIn}</p>
              </Col>
            </Form.Group>

            <Form.Group as={Row}>
              <Form.Label sm={3} column>
                Deferred Payment
              </Form.Label>
              <Col sm={3}>
                <p className="form-control-plaintext">{this.props.deferredPayment}</p>
              </Col>
            </Form.Group>

            {this.props.isLease && (
              <Fragment>
                <h3>Lease</h3>
                <Form.Group as={Row}>
                  <Form.Label sm={3} column>
                    Residual Value
                  </Form.Label>
                  <Col sm={3}>
                    <p className="form-control-plaintext">
                      {formatMoney(this.props.residualValue)}
                    </p>
                  </Col>
                </Form.Group>
              </Fragment>
            )}

            {this.isPppOrPmp && (
              <Fragment>
                <h3>PPP (Payment Per Print)</h3>
                <Form.Group as={Row}>
                  <Form.Label sm={3} column>
                    Adjustment Period
                  </Form.Label>
                  <Col sm={3}>
                    <p className="form-control-plaintext">
                      {this.props.adjustmentPeriod.replace("_", " ")}
                    </p>
                  </Col>
                </Form.Group>
                <Form.Group as={Row}>
                  <Form.Label sm={3} column>
                    Canon Copy Plan Type
                  </Form.Label>
                  <Col sm={3}>
                    <p className="form-control-plaintext">
                      {this.props.canonCopyPlanType}
                    </p>
                  </Col>
                </Form.Group>
              </Fragment>
            )}

            {this.props.isPppCombined && this.renderPppCombined()}
            {this.props.isPppStandard && this.renderPppStandard()}
            {this.props.isPmp && this.renderPmp()}
            {!IS_NZ && <PayoutInfoTable />}
          </Fragment>
        )}
      </Fragment>
    );
  }

  renderPppCombined() {
    return (
      <Table>
        <thead>
          <tr>
            <th />
            <th>Volume per Period</th>
            <th>Rent Copy Cost</th>
            <th>Service Copy Cost</th>
            <th>Total Copy Cost (excl. GST)</th>
            <th>Agreed Click Volume</th>
            <th>Adjust Click Charge Per month</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>B & W</th>
            <td>{this.props.cfaCcBwMinimumMonthlyClickVolume}</td>
            <td>{this.props.cfaCcBw}¢</td>
            <td>{this.props.cfaCcBwServiceCopyCost}¢</td>
            <td>{this.props.cfaCcBwTotalCostExclGst}¢</td>
            <td>{this.props.cfaCcBwAgreedTotalClickVolume}</td>
            <td>
              <InputGroup>
                <Form.Control
                  as={OnBlurInput}
                  type="number"
                  value={this.props.cfaCcBwAdjustClickChargePerMonthExclGst}
                  onConfirm={e => {
                    this.props.setCfaCcBwAdjustClickChargePerMonthExclGst(
                      e.target.value
                    );
                    this.props.setCfaCcColourAdjustClickChargePerMonthExclGst(
                      e.target.value
                    );
                  }}
                  min="0"
                />
                <InputGroup.Append>
                  <InputGroup.Text>¢</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
            </td>
          </tr>

          <tr>
            <th>Colour</th>
            <td>{this.props.cfaCcColourMinimumMonthlyClickVolume}</td>
            <td>{this.props.cfaCcColour}¢</td>
            <td>{this.props.cfaCcColourServiceCopyCost}¢</td>
            <td>{this.props.cfaCcColourTotalCostExclGst}¢</td>
            <td />
            <td />
          </tr>
        </tbody>
      </Table>
    );
  }

  renderPppStandard() {
    return (
      <Table>
        <thead>
          <tr>
            <th />
            <th>Volume per Period</th>
            <th>Rent Copy Cost</th>
            <th>Service Copy Cost</th>
            <th>Total Copy Cost (excl. GST)</th>
            <th>Agreed Click Volume</th>
            <th>Adjust Click Charge Per month</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>B & W</th>
            <td>{this.props.cfaCcBwMinimumMonthlyClickVolume}</td>
            <td>{this.props.cfaCcBw}¢</td>
            <td>{this.props.cfaCcBwServiceCopyCost}¢</td>
            <td>{this.props.cfaCcBwTotalCostExclGst}¢</td>
            <td>{this.props.cfaCcBwAgreedTotalClickVolume}</td>
            <td>
              <InputGroup>
                <Form.Control
                  as={OnBlurInput}
                  type="number"
                  value={this.props.cfaCcBwAdjustClickChargePerMonthExclGst}
                  onConfirm={e =>
                    this.props.setCfaCcBwAdjustClickChargePerMonthExclGst(
                      e.target.value
                    )
                  }
                  min="0"
                />
                <InputGroup.Append>
                  <InputGroup.Text>¢</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
            </td>
          </tr>

          <tr>
            <th>Colour</th>
            <td>{this.props.cfaCcColourMinimumMonthlyClickVolume}</td>
            <td>{this.props.cfaCcColour}¢</td>
            <td>{this.props.cfaCcColourServiceCopyCost}¢</td>
            <td>{this.props.cfaCcColourTotalCostExclGst}¢</td>
            <td>{this.props.cfaCcColourAgreedTotalClickVolume}</td>
            <td>
              <InputGroup>
                <Form.Control
                  as={OnBlurInput}
                  type="number"
                  value={this.props.cfaCcColourAdjustClickChargePerMonthExclGst}
                  onConfirm={e =>
                    this.props.setCfaCcColourAdjustClickChargePerMonthExclGst(
                      e.target.value
                    )
                  }
                  min="0"
                />
                <InputGroup.Append>
                  <InputGroup.Text>¢</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
            </td>
          </tr>
        </tbody>
      </Table>
    );
  }

  renderPmp() {
    return (
      <Fragment>
        <Table>
          <thead>
            <tr>
              <th />
              <th>Volume per Period</th>
              <th>CF Copy Cost</th>
              <th>Service Copy Cost</th>
              <th>Total Copy Cost (excl. GST)</th>
              <th>Excess Financed Copy Cost (excl. GST)</th>
              <th>Excess Total Click Charges (excl. GST)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>B & W</th>
              <td>{this.props.cfaCcBwMinimumMonthlyClickVolume}</td>
              <td>{this.props.cfaCcBw}¢</td>
              <td>{this.props.cfaCcBwServiceCopyCost}¢</td>
              <td>{this.props.cfaCcBwTotalCostExclGst}¢</td>
              <td>
                <InputGroup>
                  <Form.Control
                    as={OnBlurInput}
                    type="number"
                    value={this.props.cfaCcBwExcessFinancedCopyCost}
                    onConfirm={e =>
                      this.props.setCfaCcBwExcessFinancedCopyCost(e.target.value)
                    }
                    min="0"
                  />
                  <InputGroup.Append>
                    <InputGroup.Text>¢</InputGroup.Text>
                  </InputGroup.Append>
                </InputGroup>
              </td>
              <td>{this.props.cfaCcBwExcessTotalCostExclGst}¢</td>
            </tr>

            <tr>
              <th>Colour</th>
              <td>{this.props.cfaCcColourMinimumMonthlyClickVolume}</td>
              <td>{this.props.cfaCcColour}¢</td>
              <td>{this.props.cfaCcColourServiceCopyCost}¢</td>
              <td>{this.props.cfaCcColourTotalCostExclGst}¢</td>
              <td>
                <InputGroup>
                  <Form.Control
                    as={OnBlurInput}
                    type="number"
                    value={this.props.cfaCcColourExcessFinancedCopyCost}
                    onConfirm={e =>
                      this.props.setCfaCcColourExcessFinancedCopyCost(e.target.value)
                    }
                    min="0"
                  />
                  <InputGroup.Append>
                    <InputGroup.Text>¢</InputGroup.Text>
                  </InputGroup.Append>
                </InputGroup>
              </td>
              <td>{this.props.cfaCcColourExcessTotalCostExclGst}¢</td>
            </tr>
          </tbody>
        </Table>
        {this.renderPmpValidation()}
      </Fragment>
    );
  }

  renderPmpValidation() {
    if(!this.props.topLevelHardwareItemsWithClickCharge.length){
      return null;
    }

    return (
      <Fragment>

        <h3>Service Copy Cost Validation  {this.props.copyCostError ? <IconWarning /> : <IconSuccess />}</h3>
        {this.props.copyCostError && <p className="alert-danger">Please review and update Originator Service Copy Cost</p>}
        <Table>
          <thead>
            <tr>
              <th>Product</th>
              <th>B & W Volume</th>
              <th>B & W Click MSP (cents)</th>
              <th>Colour Volume</th>
              <th>Colour Click MSP (cents)</th>
            </tr>
          </thead>
          <tbody>
            {
              //TODO: check hardware has bw an cc click value
            }
            {this.props.topLevelHardwareItemsWithClickCharge.map(item => (
              <tr key={item.key}>
                <td>{item.name}</td>
                <td>
                  {item.defaultCcBw ?
                    <InputGroup>
                      <Form.Control
                        as={OnBlurInput}
                        type="number"
                        value={item.userClickVolumeBw}
                        onConfirm={e =>
                          this.props.setQuoteItemUserClickVolumeBw(item, e.target.value)
                        }
                        min="0"
                      />
                    </InputGroup> : null
                  }
                </td>
                <td>{item.defaultCcBw}¢</td>
                <td>
                  {item.defaultCcColour ?
                    <InputGroup>
                      <Form.Control
                        as={OnBlurInput}
                        type="number"
                        value={item.userClickVolumeColour}
                        onConfirm={e =>
                          this.props.setQuoteItemUserClickVolumeColour(item, e.target.value)
                        }
                        min="0"
                      />
                    </InputGroup> : null
                  }
                </td>
                <td>{item.defaultCcColour}¢</td>
              </tr>
            ))}

            <tr>
              <td />
              <td>
                <strong>Total BW Volume</strong><br/>
                {this.props.userTotalClickVolumeBw}
              </td>
              <td>
                <strong>Recommended BW Service Copy Cost</strong><br/>
                {this.props.recommendServiceCopyCostBw}
              </td>
              <td>
                <strong>Total Colour Volume</strong><br/>
                {this.props.userTotalClickVolumeColour}
              </td>
              <td>
                <strong>Recommended Colour Service Copy Cost</strong><br/>
                {this.props.recommendServiceCopyCostColour}
              </td>
            </tr>
          </tbody>
        </Table>
      </Fragment>
    );
  }
}

export default connect(
  state => ({
    originatorReference: state.quote.finance.originatorReference,
    isConfigured: state.quote.finance.isConfigured,

    financeSubtype: state.quote.finance.financeSubtype,

    cfaAmount: state.quote.finance.cfaAmount,
    totalAmountFinanced: state.quote.finance.totalAmountFinanced,

    canonFinancePayout: state.quote.finance.canonFinancePayout,
    thirdPartyPayout: state.quote.finance.thirdPartyPayout,

    financeTerm: state.quote.finance.financeTerm,
    instalmentAmount: state.quote.finance.instalmentAmount,
    instalmentType: state.quote.finance.instalmentType,
    payableIn: state.quote.finance.payableIn,

    deferredPayment: state.quote.finance.deferredPayment,

    // Lease only
    residualValue: state.quote.finance.residualValue,

    // region PPP only
    canonCopyPlanType: state.quote.finance.canonCopyPlanType,
    adjustmentPeriod: state.quote.finance.adjustmentPeriod,

    cfaCcBw: state.quote.finance.cfaCcBw,
    cfaCcBwAdjustClickChargePerMonthExclGst:
      state.quote.finance.cfaCcBwAdjustClickChargePerMonthExclGst,
    cfaCcBwMinimumMonthlyClickVolume:
      state.quote.finance.cfaCcBwMinimumMonthlyClickVolume,
    cfaCcBwServiceCopyCost: state.quote.finance.cfaCcBwServiceCopyCost,
    cfaCcBwTotalCostExclGst: cfaCcBwTotalCostExclGst(state),
    cfaCcBwAgreedTotalClickVolume: cfaCcBwAgreedTotalClickVolume(state),
    cfaCcBwExcessFinancedCopyCost: state.quote.finance.cfaCcBwExcessFinancedCopyCost,
    cfaCcBwExcessTotalCostExclGst: cfaCcBwExcessTotalCostExclGst(state),


    cfaCcColour: state.quote.finance.cfaCcColour,
    cfaCcColourAdjustClickChargePerMonthExclGst:
      state.quote.finance.cfaCcColourAdjustClickChargePerMonthExclGst,
    cfaCcColourMinimumMonthlyClickVolume:
      state.quote.finance.cfaCcColourMinimumMonthlyClickVolume,
    cfaCcColourServiceCopyCost: state.quote.finance.cfaCcColourServiceCopyCost,
    cfaCcColourTotalCostExclGst: cfaCcColourTotalCostExclGst(state),
    cfaCcColourAgreedTotalClickVolume: cfaCcColourAgreedTotalClickVolume(state),
    cfaCcColourExcessFinancedCopyCost: state.quote.finance.cfaCcColourExcessFinancedCopyCost,
    cfaCcColourExcessTotalCostExclGst: cfaCcColourExcessTotalCostExclGst(state),
    cfaBaseCharge: cfaBaseCharge(state),

    userTotalClickVolumeBw: userTotalClickVolumeBw(state),
    userTotalClickVolumeColour: userTotalClickVolumeColour(state),
    recommendServiceCopyCostBw: recommendServiceCopyCostBw(state),
    recommendServiceCopyCostColour: recommendServiceCopyCostColour(state),
    // endregion

    isLease: isLease(state),
    isPppCombined: isPppCombined(state),
    isPppStandard: isPppStandard(state),
    isPmp: isPmp(state),
    isOriginatorReferenceValid: isOriginatorReferenceValid(state),
    isPppOrPmp: isPppOrPmp(state),

    topLevelHardwareItemsWithClickCharge: topLevelHardwareItemsWithClickCharge(state),
    copyCostError: validateServiceCopyCost(state)
  }),
  {
    setIsFinanceConfigured,
    setFinanceType,
    setFinanceSubtype,
    setCanonCopyPlanType,
    setCfaAmount,
    setTotalAmountFinanced,
    setAdjustmentPeriod,
    setCanonFinancePayout,
    setCfaCcBw,
    setCfaCcBwAdjustClickChargePerMonthExclGst,
    setCfaCcBwMinimumMonthlyClickVolume,
    setCfaCcBwServiceCopyCost,
    setCfaCcBwExcessFinancedCopyCost,
    setCfaCcColour,
    setCfaCcColourAdjustClickChargePerMonthExclGst,
    setCfaCcColourMinimumMonthlyClickVolume,
    setCfaCcColourServiceCopyCost,
    setCfaCcColourExcessFinancedCopyCost,
    setFinanceTerm,
    setServiceTerm,
    setInstalmentAmount,
    setInstalmentType,
    setOriginatorReference,
    setPayableIn,
    setResidualValue,
    setThirdPartyPayout,
    setDeferredPayment,
    setQuoteItemUserClickVolumeBw,
    setQuoteItemUserClickVolumeColour
  }
)(Originator);
